import { NavigationComponent } from '../view/navigation.component';
import { CollapseComponent } from '../view/collapse.component';
import { LayoutComponent } from '../view/layout.component';
import { WidgetComponent } from '../view/widget.component';
import { DashboardConfig } from './config/dashboard.config';
import { RegisteredComponent } from './config/components';
import { SYSTEM_CLASSES } from '../view/dom.constant';
import { copy, isDefined, uuid } from './utils';
import { DashboardNodeType } from './node.type';
import { DashboardState } from './state';
import { NodeArgs } from './node.args';
import { JSON } from './json.type';
import {
  InvalidContainerChildrenError,
  MissingComponentIdError,
} from './errors/container.error';

export abstract class DashboardNode {
  private readonly systemClass = SYSTEM_CLASSES;
  public readonly config: DashboardConfig;
  private _parent!: DashboardNode | null;
  private _toolbarVisible: boolean;
  protected _classes: string[];
  public readonly id: string;
  public readonly data: any;
  private focuseTimeout: any;
  public name: string;

  get parent(): DashboardNode | null {
    return this._parent;
  }

  get classes(): string[] {
    return copy(this._classes);
  }

  get toolbarVisible(): boolean {
    return this._toolbarVisible;
  }

  constructor(protected args: NodeArgs<any>) {
    this._toolbarVisible = false;
    this.data = copy(args.data);
    this._parent = args.parent;
    this.config = args.config;
    this._classes = [];

    if (!isDefined(args.config)) {
      throw new Error('Missing dashboard config');
    }

    if (args.data instanceof DashboardNode) {
      throw new Error('Data cannot be instance of DashboardNode');
    }

    const customNameField = this.config.node?.name;
    const customIdField = this.config.node?.id;
    const id = customIdField
      ? this.getFieldValue(customIdField, args.data)
      : args.data.id;

    this.id = args.useCoreId ? uuid() : id || uuid();
    this.name = customNameField
      ? this.getFieldValue(customNameField, args.data)
      : args.data.name || `node_${uuid()}`;

    if (this.config.focusedNodeId === this.id) {
      this.focus();
    }
  }

  protected getFieldValue(name: string, object: any): any {
    return name?.split('.').reduce((p, c) => (p && p[c]) || null, object);
  }

  /**
   *
   * @param classes List of classes to add
   */
  public addClasses(classes: string[]): void {
    classes
      ?.filter((c) => !this.systemClass.includes(c))
      .forEach((name) => {
        if (this._classes.indexOf(name) < 0) {
          this._classes.push(name);
        }
      });
  }

  /**
   *
   * @param classes List of classes to remove
   */
  public removeClasses(classes: string[]): void {
    this._classes =
      this._classes
        ?.filter((c) => !this.systemClass.includes(c))
        ?.filter((c) => !classes.includes(c)) || [];
  }

  public focus(): void {
    clearTimeout(this.focuseTimeout);
    this.focuseTimeout = setTimeout(() => {
      this.executeFocus(this);
    }, 500);
  }

  private executeFocus(
    target: DashboardNode | null,
    previous?: DashboardNode
  ): void {
    if (target) {
      if (target instanceof Navigation && previous) {
        target.selectNavigation(previous.id);
      } else if (target instanceof ExpandableNode) {
        target.expand();
      }

      this.executeFocus(target.parent, target);
    }
  }

  protected executeToolbarToggle(): void {
    if (this.config.allowDrag) {
      this._toolbarVisible = !this._toolbarVisible;
    }
  }

  protected executeMoveTo(
    container: Container,
    node: Layout | Collapse | Widget,
    index: number
  ): void {
    if (this.parent instanceof Container) {
      const sourceIndex = this.parent.items.findIndex((n) => n.id === this.id);
      if (container.exist(node, { deep: false })) {
        if (sourceIndex >= 0) {
          const newIndex = index > sourceIndex ? index - 1 : index;
          this.submitDropEvent(container, newIndex, node, sourceIndex, {
            isTransfertToArray: false,
            isMovingInArray: true,
          });
          container.items.splice(sourceIndex, 1);
          container.items.splice(newIndex, 0, node);
        }
      } else {
        this.submitDropEvent(container, index, node, sourceIndex, {
          isTransfertToArray: false,
          isMovingInArray: false,
        });
        this.parent.items.splice(sourceIndex, 1);
        this._parent = container; // Change parent after delete !!
        container.items.splice(index, 0, node);
      }
    }
  }

  protected executeDelete(): void {
    if (this.parent instanceof Container) {
      const index = this.parent.items.findIndex((n) => n.id === this.id);
      if (index > -1) {
        this.parent.items.splice(index, 1);
      }
    }
  }

  protected submitDropEvent(
    target: Container,
    targetIndex: number,
    node: Layout | Collapse | Widget,
    sourceIndex: number | undefined,
    args: {
      isMovingInArray: boolean;
      isTransfertToArray: boolean;
    }
  ): void {
    this.config.drop?.call(null, {
      isTransfertToArray: args.isTransfertToArray,
      isMovingInArray: args.isMovingInArray,
      source: (<Container>this.parent)?.items,
      sourceIndex: sourceIndex,
      targetIndex: targetIndex,
      target: target,
      node: node,
    });
  }

  public toJSON(): JSON<any> {
    return copy(this.data);
  }
}

// ============================================== //
//                 DRAGGABLE
// ============================================== //
export interface Draggable {
  readonly component: RegisteredComponent;
  get isDraggable(): boolean;
  moveTo(container: Container, index: number): void;
  delete(): void;
  clone(): DashboardNode;
  toggleToolbar(): void;
}

export abstract class HierachicNode<
  T extends DashboardNode
> extends DashboardNode {
  protected readonly children: T[] = [];

  constructor(args: NodeArgs<any>) {
    super(args);
  }

  /**
   * To change container position
   * @param id container id
   * @param targetIndex new container index
   */
  protected moveContainer(id: string, targetIndex: number): void {
    const index = this.children.findIndex((n) => n.id === id);
    if (index > -1) {
      this.children.splice(targetIndex, 0, this.children[index]);
      this.children.splice(index, 1);
    }
  }

  public exist(node: T, strategy?: { deep: boolean }): boolean {
    if (this.children?.length === 0) {
      return false;
    }

    return this.executeExistCheck(this.children, node, strategy);
  }

  private executeExistCheck(
    nodes: T[],
    target: T,
    strategy?: { deep: boolean }
  ): boolean {
    let found = false;
    for (const node of nodes) {
      if (node.id === target.id) {
        return true;
      } else if (strategy?.deep && node instanceof HierachicNode) {
        found = this.executeExistCheck(node.children, target);
      }
    }

    return found;
  }

  public override toJSON(): JSON<any> {
    const data = copy(this.data);
    data.children = this.children.map((n) => n.toJSON()) || [];

    return data;
  }
}

// ============= CONTAINER =========== //

export class Container extends HierachicNode<Layout | Collapse | Widget> {
  get isDroppable(): boolean {
    return this.config.canDrop
      ? this.config.canDrop?.call(null, this)
      : this.config.allowDrag === true;
  }

  get items(): Array<Layout | Collapse | Widget> {
    return this.children;
  }

  constructor(args: NodeArgs<Layout | Collapse | null>) {
    super(args);
    this._classes.push(DashboardNodeType.CONTAINER);
    if (Array.isArray(args.data.children)) {
      (args.data.children as Array<any>)?.forEach((node) =>
        this.children.push(this.getNodeInstance(node))
      );
    }
  }

  public addNode(node: any, index: number): void {
    const dataNode = node instanceof DashboardNode ? node.data : node;
    if (dataNode) {
      const nodeInstance = this.getNodeInstance(dataNode);
      this.submitDropEvent(this, index, nodeInstance, undefined, {
        isTransfertToArray: true,
        isMovingInArray: false,
      });
      this.items.splice(index, 0, nodeInstance);
    }
  }

  private getNodeInstance(data: any): Layout | Collapse | Widget {
    const customComponentIdField = this.config.node?.componentId;
    const componentId = customComponentIdField
      ? this.getFieldValue(customComponentIdField, data)
      : data.componentId;

    try {
    } catch (e) {}

    if (!componentId) {
      throw new MissingComponentIdError(data);
    }

    const component = this.config.components.get(componentId);
    if (!component) {
      throw new Error(`Component ${componentId} not found !`);
    }

    const args = {
      config: this.config,
      data: copy(data),
      parent: this,
      component,
    } as NodeArgs<any>;

    if (new (<any>component).class() instanceof NavigationComponent) {
      return new Navigation(args);
    } else if (new (<any>component).class() instanceof LayoutComponent) {
      return new Layout(args);
    } else if (new (<any>component).class() instanceof WidgetComponent) {
      return new Widget(args);
    } else if (new (<any>component).class() instanceof CollapseComponent) {
      return new Collapse(args);
    } else {
      throw new InvalidContainerChildrenError(data);
    }
  }
}

// ============= LAYOUT ============= //

export class Layout extends HierachicNode<Container> implements Draggable {
  public readonly component: RegisteredComponent;

  get isDraggable(): boolean {
    return this.config.canDrag
      ? this.config.canDrag.call(null, this)
      : this.config.allowDrag === true;
  }

  get containers(): Container[] {
    return this.children;
  }

  constructor(args: NodeArgs<Container>) {
    super(args);
    this._classes.push(DashboardNodeType.LAYOUT);
    this.component = args.component;

    if (Array.isArray(args.data.children)) {
      (args.data.children as Array<any>).forEach((node) => {
        this.children.push(
          new Container({
            component: this.component,
            config: args.config,
            parent: this,
            data: node,
          })
        );
      });
    }
  }
  select(): void {
    throw new Error('Method not implemented.');
  }

  delete(): void {
    this.executeDelete();
  }

  toggleToolbar(): void {
    this.executeToolbarToggle();
  }

  clone(): Layout {
    return new Layout({
      component: this.args.component,
      config: this.args.config,
      parent: this.args.parent,
      data: this.args.data,
      useCoreId: true,
    });
  }

  moveTo(container: Container, index: number): void {
    this.executeMoveTo(container, this, index);
  }
}

// ============== NAVIGATION =================//

export class Navigation extends Layout {
  private _activeNavigation: Container | undefined;

  get activeNavigation(): Container | undefined {
    return this._activeNavigation;
  }

  get navs(): Container[] {
    return this.children;
  }

  constructor(args: NodeArgs<Container>) {
    super(args);
    this._classes.push(DashboardNodeType.NAVIGATION);
    this._activeNavigation = this.navs[0];
    const stateNavId = (<DashboardState>this.config.state).activeNavigations[
      this.id
    ];

    if (stateNavId && stateNavId !== this._activeNavigation.id) {
      this.selectNavigation(stateNavId);
    }
  }

  /**
   *
   * @param id navigation id
   */
  selectNavigation(id: string): void {
    this._activeNavigation = this.navs.find((nav) => nav.id === id);
    if (this._activeNavigation) {
      (<DashboardState>this.config.state).activeNavigations[this.id] = id;
      (<DashboardState>this.config.state).focusedNavigation = {
        id: this._activeNavigation.id,
        name: this._activeNavigation.name,
      };
      this.config.stateChange?.call(null, <DashboardState>this.config.state);
    }
  }

  addNavigation(params: { name: string }): void {
    this.children.push(
      new Container({
        component: {} as any,
        config: this.config,
        parent: this,
        data: {
          name: params.name,
          children: [],
        },
      })
    );
  }

  /**
   *
   * @param id navigation id
   */
  removeNavigation(id: string): void {
    const index = this.navs.findIndex((n) => n.id === id);
    if (index > -1) {
      this.children.splice(index, 1);
    }
  }

  /**
   * To change navigation position
   * @param id navaigation id
   * @param targetIndex new navaigation position
   */
  public moveNavigation(id: string, targetIndex: number): void {
    this.moveContainer(id, targetIndex);
  }

  override clone(): Navigation {
    return new Navigation({
      component: this.args.component,
      config: this.args.config,
      parent: this.args.parent,
      data: this.args.data,
      useCoreId: true,
    });
  }
}

// ==============================================
//            EXPANDABLE NODES
// ==============================================

export abstract class ExpandableNode extends DashboardNode {
  private _isExpanded: boolean = true;

  get isDraggable(): boolean {
    return this.config.canDrag
      ? this.config.canDrag.call(null, this)
      : this.config.allowDrag === true;
  }

  get isExpanded(): boolean {
    return this._isExpanded;
  }

  constructor(args: NodeArgs<any>) {
    super(args);
    if (
      (<DashboardState>this.config.state).expandableNodes[this.id] === false
    ) {
      this.collapse({ stateIgnore: true });
    }
  }

  public toggle(): void {
    this.isExpanded ? this.collapse() : this.expand();
  }

  public expand(args?: { stateIgnore: boolean }): void {
    this._isExpanded = true;
    if (!args?.stateIgnore) {
      this.saveState();
    }
  }

  public collapse(args?: { stateIgnore: boolean }): void {
    this._isExpanded = false;
    if (!args?.stateIgnore) {
      this.saveState();
    }
  }

  public delete(): void {
    this.executeDelete();
  }

  private saveState(): void {
    (<DashboardState>this.config.state).expandableNodes[this.id] =
      this.isExpanded;
    this.config.stateChange?.call(null, <DashboardState>this.config.state);
  }
}

// ================ COLLAPSE ============== //

export class Collapse extends ExpandableNode implements Draggable {
  public readonly component: RegisteredComponent;
  public readonly container: Container;

  constructor(args: NodeArgs<Container>) {
    super(args);
    this._classes.push(DashboardNodeType.COLLAPSE);
    this.component = args.component;

    const children = args.data.children;
    this.container =
      Array.isArray(children) && children.length > 0
        ? new Container({
            component: this.component,
            config: args.config,
            data: children[0],
            parent: this,
          })
        : new Container({
            component: this.component,
            config: args.config,
            parent: this,
            data: { id: uuid(), name: 'Container' },
          });
  }
  select(): void {
    throw new Error('Method not implemented.');
  }

  clone(): Collapse {
    return new Collapse({
      component: this.args.component,
      config: this.args.config,
      parent: this.args.parent,
      data: this.args.data,
      useCoreId: true,
    });
  }

  toggleToolbar(): void {
    this.executeToolbarToggle();
  }

  moveTo(container: Container, index: number): void {
    this.executeMoveTo(container, this, index);
  }

  public override toJSON(): JSON<any> {
    const data = copy(this.data);
    data.children = [this.container.toJSON()];

    return data;
  }
}

// ================ WIDGET ============== //

export class Widget extends ExpandableNode implements Draggable {
  public readonly component: RegisteredComponent;

  constructor(args: NodeArgs<Container>) {
    super(args);
    this._classes.push(DashboardNodeType.WIDGET);
    this.component = args.component;
  }

  clone(): Widget {
    return new Widget({
      component: this.args.component,
      config: this.args.config,
      parent: this.args.parent,
      data: copy(this.args.data),
      useCoreId: true,
    });
  }

  toggleToolbar(): void {
    this.executeToolbarToggle();
  }

  /**
   *
   * @param container Target container
   * @param index Position in container
   */
  moveTo(container: Container, index: number): void {
    this.executeMoveTo(container, this, index);
  }
}
